import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CampaignOutlined from '@mui/icons-material/CampaignOutlined';

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusColor = (status) => {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'SENT':
      return 'info';
    default:
      return 'default';
  }
};

const ReadOnlyAnnouncementCard = ({ announcement, colors }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const cardBg = colors.primary[400];
  const titleColor = isDark ? '#ffffff' : colors.grey[100];
  const descColor = isDark ? colors.grey[200] : colors.primary[600];
  const metaColor = isDark ? colors.grey[300] : colors.grey[400];
  const messagePanelBg = isDark ? alpha('#ffffff', 0.14) : '#ffffff';
  const messageTextColor = isDark ? '#ffffff' : colors.primary[600];
  const messageLabelColor = isDark ? colors.greenAccent[300] : colors.blueAccent[600];
  const cardBorder = `1px solid ${alpha(colors.grey[100], isDark ? 0.08 : 0.16)}`;

  return (
    <Card
      sx={{
        width: { xs: '100%', sm: 300 },
        minHeight: 240,
        borderRadius: '16px',
        backgroundColor: cardBg,
        border: cardBorder,
        boxShadow: isDark
          ? '0 8px 22px rgba(0,0,0,0.28)'
          : '0 10px 24px rgba(20, 27, 45, 0.18)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box display="flex" alignItems="flex-start" gap={1.25}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(colors.blueAccent[500], 0.28),
              flexShrink: 0,
            }}
          >
            <CampaignOutlined sx={{ color: colors.greenAccent[400], fontSize: 20 }} />
          </Box>
          <Box flex={1} minWidth={0}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: titleColor, lineHeight: 1.3 }}>
              {announcement.messageTitle}
            </Typography>
            {announcement.className && (
              <Typography variant="caption" sx={{ color: metaColor, fontWeight: 600, display: 'block', mt: 0.5 }}>
                {announcement.className}
              </Typography>
            )}
          </Box>
        </Box>

        {announcement.messageDescription && (
          <Typography
            variant="body2"
            sx={{
              mt: 1,
              color: descColor,
              fontWeight: 500,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.45,
            }}
          >
            {announcement.messageDescription}
          </Typography>
        )}

        <Box
          sx={{
            mt: 1.25,
            p: 1.25,
            borderRadius: '12px',
            bgcolor: messagePanelBg,
            border: `1px solid ${alpha(isDark ? '#ffffff' : colors.primary[600], isDark ? 0.16 : 0.12)}`,
            flex: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: messageLabelColor,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              fontWeight: 800,
              fontSize: '0.68rem',
            }}
          >
            Ujumbe
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mt: 0.75,
              color: messageTextColor,
              whiteSpace: 'pre-wrap',
              lineHeight: 1.6,
              wordBreak: 'break-word',
              fontSize: '0.92rem',
              fontWeight: isDark ? 500 : 600,
            }}
          >
            {announcement.messageBody}
          </Typography>
        </Box>

        <Box mt={1.25}>
          <Chip
            label={announcement.status || 'ACTIVE'}
            size="small"
            color={getStatusColor(announcement.status)}
            sx={{ height: 24, fontSize: '0.72rem', fontWeight: 700, color: '#fff' }}
          />
          <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: metaColor, fontWeight: 500 }}>
            {formatDate(announcement.createdAt)}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ReadOnlyAnnouncementCard;
